'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { useCallback, useRef } from 'react'
import { RotateCcwIcon, SearchIcon, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { TabSelect } from '@/components/TabSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { legislativeTabs, useLegislativeContext } from '@/providers/LegislativeProvider'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import { FilterButton } from './FilterButton'
import { StateSelect } from './StateSelect'

import type { Topic } from '@/payload-types'
import type { MahaStance } from '@/providers/LegislativeProvider'

type LegislativeTrackerSidebarProps = {
  isScoped: boolean
}

export const LegislativeTrackerSidebar = ({ isScoped }: LegislativeTrackerSidebarProps) => {
  const {
    selectedLegislativeTab,
    setSelectedLegislativeTab,
    selectedState,
    setSelectedState,
    searchTerm,
    setSearchTerm,
    billTopics,
    selectedBillTopics,
    setSelectedBillTopics,
    mahaStance,
    setMahaStance,
    mahaApprovalCounts,
  } = useLegislativeContext()
  const { isMobile } = useLenisContext()
  const router = useRouter()
  const pathname = usePathname()

  const searchInputRef = useRef<HTMLInputElement>(null)
  const stateSelectMobileRef = useRef<HTMLSelectElement>(null)
  const stateSelectDesktopRef = useRef<HTMLButtonElement>(null)

  const shouldSyncUrl =
    pathname?.startsWith('/legislative-tracker/bills') &&
    !pathname?.match(/\/legislative-tracker\/bills\/[^/]+\/[^/]+$/)

  const pushIfNeeded = useCallback(
    (targetPath: string) => {
      if (shouldSyncUrl && pathname !== targetPath) {
        router.push(targetPath)
      }
    },
    [pathname, router, shouldSyncUrl],
  )

  const handleTabChange = useCallback(
    (tabValue: string) => {
      setSelectedLegislativeTab(tabValue)

      if (tabValue !== 'state') {
        setSelectedState(undefined)
      }

      if (tabValue === 'all-bills') {
        pushIfNeeded('/legislative-tracker/bills/all')
      } else if (tabValue === 'us') {
        pushIfNeeded('/legislative-tracker/bills/us')
      } else if (tabValue === 'state' && selectedState) {
        pushIfNeeded(`/legislative-tracker/bills/${selectedState.toLowerCase()}`)
      }
    },
    [pushIfNeeded, selectedState, setSelectedLegislativeTab, setSelectedState],
  )

  const handleStateChange = useCallback(
    (value: string) => {
      setSelectedState(value)
      pushIfNeeded(`/legislative-tracker/bills/${value.toLowerCase()}`)
    },
    [pushIfNeeded, setSelectedState],
  )

  const handleTopicToggle = useCallback(
    (topic: Topic | null) => {
      if (topic === null) {
        setSelectedBillTopics([])
        setMahaStance(null)
        return
      }
      if (selectedBillTopics.includes(topic)) {
        setSelectedBillTopics(selectedBillTopics.filter((t) => t.id !== topic.id))
      } else {
        setSelectedBillTopics([...selectedBillTopics, topic])
      }
    },
    [selectedBillTopics, setSelectedBillTopics, setMahaStance],
  )

  const handleStanceToggle = useCallback(
    (stance: MahaStance) => {
      setMahaStance(mahaStance === stance ? null : stance)
    },
    [mahaStance, setMahaStance],
  )

  const viewMoreBillsHref = (() => {
    if (selectedLegislativeTab === 'us') {
      return '/legislative-tracker/bills/us'
    }
    if (selectedLegislativeTab === 'state' && selectedState) {
      return `/legislative-tracker/bills/${selectedState.toLowerCase()}`
    }
    return '/legislative-tracker/bills/all'
  })()

  const showStanceFilters = isScoped

  return (
    <>
      {/* Search and filter controls - only shown on scoped pages */}
      <div
        className={cn(
          'flex w-full flex-col items-start gap-6 [&>div]:w-full',
          !isScoped && 'hidden',
        )}
      >
        <Input
          ref={searchInputRef}
          placeholder="Search"
          IconComponent={SearchIcon}
          className="w-full items-center"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-family-body text-base font-semibold text-muted-foreground">
            Filters
          </h3>
          <Button
            variant="iconOutline"
            size="small"
            customIcon={<RotateCcwIcon className="size-4.5" />}
            title="Clear filters"
            onClick={() => handleTopicToggle(null)}
            disabled={selectedBillTopics.length === 0 && !mahaStance}
          />
        </div>
      </div>

      {/* Tab selector */}
      <TabSelect
        instanceKey={
          isScoped
            ? 'legislative-tracker-sidebar-tabs-scoped'
            : 'legislative-tracker-sidebar-tabs-unscoped'
        }
        variant={isScoped ? 'default' : 'evergreen'}
        tabs={legislativeTabs}
        value={selectedLegislativeTab}
        onTabChange={handleTabChange}
      />

      {/* State select - animated in when state tab selected */}
      <AnimatePresence mode="popLayout">
        {selectedLegislativeTab === 'state' && (
          <motion.div
            key={selectedLegislativeTab}
            initial={
              selectedLegislativeTab === 'state' && selectedState ? false : { opacity: 0, y: -10 }
            }
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            onAnimationComplete={() => {
              if (selectedState === undefined || selectedState === '') {
                if (isMobile) {
                  stateSelectMobileRef.current?.focus()
                } else {
                  stateSelectDesktopRef.current?.focus()
                }
              }
            }}
            className={cn('w-full', !isScoped && 'mt-4')}
          >
            <StateSelect
              stateSelectMobileRef={stateSelectMobileRef as React.RefObject<HTMLSelectElement>}
              stateSelectDesktopRef={stateSelectDesktopRef as React.RefObject<HTMLButtonElement>}
              selectedState={selectedState}
              handleStateChange={handleStateChange}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {showStanceFilters && (
        <div className="mt-2 flex w-full flex-col gap-2">
          <Button
            variant="filter"
            className={cn(mahaStance === 'supported' && 'selected')}
            onClick={() => handleStanceToggle('supported')}
            disabled={mahaApprovalCounts.supported === 0}
          >
            <span className="flex items-center gap-2">
              <ThumbsUpIcon className="size-4.5" />
              MAHA Supported
            </span>
            <span className="text-xs font-normal">{mahaApprovalCounts.supported}</span>
          </Button>
          <Button
            variant="filter"
            className={cn(mahaStance === 'opposed' && 'selected')}
            onClick={() => handleStanceToggle('opposed')}
            disabled={mahaApprovalCounts.opposed === 0}
          >
            <span className="flex items-center gap-2">
              <ThumbsDownIcon className="size-4.5" />
              MAHA Opposed
            </span>
            <span className="text-xs font-normal">{mahaApprovalCounts.opposed}</span>
          </Button>
        </div>
      )}

      {/* View more bills link - only shown on non-scoped pages */}
      <Button asChild variant="outline" className={cn('mt-4 w-full', isScoped && 'hidden')}>
        <Link href={viewMoreBillsHref}>View more bills</Link>
      </Button>

      {/* Topic filters - only shown on scoped pages */}
      <AnimatePresence>
        {isScoped && (
          <motion.div className="mt-2 flex w-full flex-col gap-2">
            <div className="flex w-full flex-col gap-2 border-t border-border pt-4">
              {billTopics.map((topic) => (
                <FilterButton
                  key={topic.id}
                  topic={topic}
                  handleTopicClick={handleTopicToggle}
                  selectedBillTopics={selectedBillTopics}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
